import { AvailabilityType } from '@wix/ambassador-restaurants-menu-settings-v1-menu-ordering-settings/types';
import { DateTime } from 'luxon';
import type { WeeklyAvailability } from '@wix/restaurants-olo-operations-client-commons';
import { getWeeklyAvailabilitySummary } from '@wix/restaurants-olo-operations-client-commons';
import type { CommonAvailability } from 'root/types/availability';
import type { TFunction } from '@wix/yoshi-flow-editor';
import type {
  AvailabilityStatusKeys,
  AvailabilityStatusKeysConfig,
} from 'root/availabilityStatusKeys';
import type { MenuState } from 'root/states/MenuState';
import { getSafeDate } from './dateTimeUtils';

export type AvailabilityData =
  | { type: 'timestamp_ranges'; timestampRange: { startTime: Date; endTime: Date } }
  | { type: 'weekly_schedule'; weeklyAvailability: WeeklyAvailability }
  | { type: 'always_available' }
  | { type: 'none' };

const extractScheduledAvailabilityData = (
  availability: CommonAvailability | undefined,
  timezone: string = 'America/New_York'
): AvailabilityData => {
  if (!availability) {
    return { type: 'none' };
  }

  if (availability.type === AvailabilityType.TIMESTAMP_RANGES) {
    const ranges = availability.timestampRangesOptions?.ranges;
    if (!ranges?.length) {
      return { type: 'none' };
    }

    const now = DateTime.now().setZone(timezone).startOf('day').valueOf();
    const availableRange = ranges.find((range) => {
      const endTime = getSafeDate(range.endTime);
      return endTime?.valueOf() >= now;
    });

    if (!availableRange) {
      return { type: 'none' };
    }

    const startTime = getSafeDate(availableRange.startTime);
    const endTime = getSafeDate(availableRange.endTime);

    if (!startTime || !endTime) {
      return { type: 'none' };
    }

    return {
      type: 'timestamp_ranges',
      timestampRange: { startTime, endTime },
    };
  } else if (availability.type === AvailabilityType.WEEKLY_SCHEDULE) {
    const weeklyAvailability = availability.weeklyScheduleOptions?.weeklyAvailability;
    if (!weeklyAvailability) {
      return { type: 'none' };
    }

    return {
      type: 'weekly_schedule',
      weeklyAvailability,
    };
  } else if (availability.type === AvailabilityType.ALWAYS_AVAILABLE) {
    return { type: 'always_available' };
  } else {
    return { type: 'none' };
  }
};

const formatAvailabilityData = ({
  availabilityData,
  locale,
  keys,
  t,
}: {
  availabilityData: AvailabilityData;
  locale: string;
  keys: AvailabilityStatusKeys;
  t: TFunction;
}): {
  shouldCollapseAvailabilityStatus?: boolean;
  text?: string;
} => {
  if (availabilityData.type === 'timestamp_ranges' && availabilityData.timestampRange) {
    const text = formatDateRanges({
      startDate: availabilityData.timestampRange.startTime,
      endDate: availabilityData.timestampRange.endTime,
      locale,
      keys,
      t,
    });

    return { shouldCollapseAvailabilityStatus: false, text };
  }

  if (availabilityData.type === 'weekly_schedule' && availabilityData.weeklyAvailability) {
    const weeklyAvailabilitySummary = getWeeklyAvailabilitySummaryText({
      availability: availabilityData.weeklyAvailability,
      locale,
      t,
    });

    const text = weeklyAvailabilitySummary
      ? t(keys.weeklyScheduleText, { weeklyAvailabilitySummary })
      : undefined;

    return { shouldCollapseAvailabilityStatus: !text, text };
  }

  return { shouldCollapseAvailabilityStatus: true };
};

const getMenuAvailabilityStatusText = ({
  availability,
  locale,
  timezone,
  keys,
  t,
}: {
  availability: CommonAvailability;
  locale: string;
  timezone: string;
  keys: AvailabilityStatusKeys;
  t: TFunction;
}): {
  shouldCollapseAvailabilityStatus?: boolean;
  text?: string;
} => {
  const availabilityData = extractScheduledAvailabilityData(availability, timezone);

  const result = formatAvailabilityData({
    availabilityData,
    locale,
    keys,
    t,
  });

  return result;
};

const formatDateRanges = ({
  startDate,
  endDate,
  locale,
  keys,
  t,
}: {
  startDate?: Date;
  endDate?: Date;
  locale: string;
  keys: AvailabilityStatusKeys;
  t: TFunction;
}) => {
  const formattedStartDate = startDate?.toLocaleString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const formattedEndDate = endDate?.toLocaleString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const key = formattedStartDate !== formattedEndDate ? keys.dateRangeText : keys.dateText;

  return t(key, {
    startDate: formattedStartDate,
    endDate: formattedEndDate,
  });
};

const getWeeklyAvailabilitySummaryText = ({
  availability,
  locale,
  t,
}: {
  availability?: WeeklyAvailability;
  locale: string;
  t: TFunction;
}) => {
  const weeklyAvailabilitySummary =
    availability &&
    getWeeklyAvailabilitySummary({
      availability,
      dayNameProvider: (day) => t(`menu_olo.menuAvailabilityStatus.${day}.short`),
      languageCode: locale,
    });
  if (!weeklyAvailabilitySummary) {
    return '';
  } else {
    const availabilityByDays: { [key: string]: string[] } = {};

    weeklyAvailabilitySummary.forEach((timeSlots, days) => {
      const slots = timeSlots.map((slot) => slot.timeSlot);

      if (slots.length > 0) {
        availabilityByDays[days] = slots;
      }
    });

    return Object.entries(availabilityByDays)
      .map(([days, timeSlots]) => {
        return `${days}: ${timeSlots.join(', ')}`;
      })
      .join('; ');
  }
};

const getAvailabilityStatusProps = ({
  menu,
  locale,
  timezone,
  t,
  keys,
}: {
  menu?: MenuState;
  locale: string;
  timezone: string;
  t: TFunction;
  keys: AvailabilityStatusKeysConfig;
}) => {
  const isMenuOfItemAvailable = menu?.isAvailable ?? true;
  const hasNextAvailability = !!menu?.hasNextAvailableTimeslot;

  const { text, shouldCollapseAvailabilityStatus } =
    menu?.getAvailabilityStatus({
      locale,
      timezone,
      t,
      keys,
    }) || {};

  return { isMenuOfItemAvailable, hasNextAvailability, text, shouldCollapseAvailabilityStatus };
};

export {
  getMenuAvailabilityStatusText,
  getWeeklyAvailabilitySummaryText,
  getAvailabilityStatusProps,
  extractScheduledAvailabilityData,
};
